import React from "react";
import { Link, NavLink, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { api, ZONES, PROPERTY_TYPES, formatAED, resolveImg, UAE_EMIRATES } from "../lib/api";
import { toast } from "sonner";
import AdminImageGallery from "../components/AdminImageGallery";
import AdminChangePassword from "./AdminChangePassword";
import AdminMarket from "./AdminMarket";
import AdminInsights from "./AdminInsights";

function AdminField({ label, value, onChange, type = "text", textarea = false, ...rest }) {
  if (textarea) {
    return (
      <div>
        <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1">{label}</label>
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
          className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]"
          {...rest}
        />
      </div>
    );
  }
  return (
    <div>
      <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1">{label}</label>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833]"
        {...rest}
      />
    </div>
  );
}

function AdminShell({ children }) {
  const navigate = useNavigate();
  const [me, setMe] = React.useState(null);
  React.useEffect(() => {
    if (!localStorage.getItem("bw_admin_token")) {
      navigate("/admin/login");
      return;
    }
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {
      localStorage.removeItem("bw_admin_token");
      navigate("/admin/login");
    });
  }, [navigate]);

  const logout = () => {
    localStorage.removeItem("bw_admin_token");
    navigate("/admin/login");
  };

  if (!me) return <div className="p-10 font-mono-tech text-xs">Authenticating...</div>;

  return (
    <div data-testid="admin-shell" className="min-h-screen bg-zinc-50 flex">
      <aside className="w-60 bg-zinc-950 text-white sticky top-0 h-screen flex flex-col">
        <div className="p-5 border-b border-zinc-800">
          <Link to="/" className="flex items-center gap-2">
            <img src="/warehousesuae-icon-white.png" alt="WarehousesUAE" className="w-8 h-8 object-contain" />
            <div>
              <div className="font-display text-sm tracking-tighter">WAREHOUSESUAE<span className="text-[#FFCC00]">.COM</span></div>
              <div className="font-mono-tech text-[9px] text-zinc-500 uppercase">Admin v1</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-2 space-y-1">
          {[
            { to: "/admin", label: "Dashboard", end: true },
            { to: "/admin/properties", label: "Properties" },
            { to: "/admin/properties/new", label: "+ Add Property" },
            { to: "/admin/import", label: "↓ Import Sheet" },
            { to: "/admin/enquiries", label: "Enquiries" },
            { to: "/admin/submissions", label: "Owner Submissions" },
            { to: "/admin/market", label: "📊 Market Analytics" },
            { to: "/admin/insights", label: "📈 Search Insights" },
            { to: "/admin/change-password", label: "🔑 Change Password" },
          ].map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `block font-mono-tech text-xs uppercase px-3 py-2 ${isActive ? "bg-[#031833] text-white" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-zinc-800 font-mono-tech text-[10px] text-zinc-500">
          <div>{me.email}</div>
          <button data-testid="admin-logout" onClick={logout} className="text-[#FFCC00] hover:underline mt-2">→ Sign out</button>
        </div>
      </aside>
      <main className="flex-1 p-6 md:p-10 overflow-x-hidden">{children}</main>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = React.useState(null);
  React.useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data));
  }, []);
  if (!stats) return <div className="font-mono-tech text-sm">Loading...</div>;
  const cards = [
    { l: "Total Properties", v: stats.total_properties, c: "#031833" },
    { l: "Available", v: stats.available, c: "#22C55E" },
    { l: "Featured", v: stats.featured, c: "#FFCC00" },
    { l: "Total Enquiries", v: stats.total_enquiries, c: "#09090B" },
    { l: "New Enquiries", v: stats.new_enquiries, c: "#EF4444" },
  ];
  return (
    <div data-testid="admin-dashboard">
      <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-2">// CONTROL ROOM</div>
      <h1 className="font-display text-4xl tracking-tighter mb-8">DASHBOARD</h1>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-zinc-200">
        {cards.map((c, i) => (
          <div key={i} className="bg-white p-5">
            <div className="font-mono-tech text-[9px] uppercase text-zinc-500 mb-2">{c.l}</div>
            <div className="font-display text-4xl tracking-tighter" style={{ color: c.c }}>{c.v}</div>
          </div>
        ))}
      </div>
      <div className="mt-10 grid md:grid-cols-2 gap-4">
        <Link to="/admin/properties/new" className="bg-zinc-950 text-white p-6 hover:bg-[#031833]">
          <div className="font-mono-tech text-[10px] uppercase opacity-70 mb-2">// QUICK ACTION</div>
          <div className="font-display text-2xl tracking-tighter">+ ADD PROPERTY</div>
        </Link>
        <Link to="/admin/enquiries" className="bg-[#FFCC00] text-zinc-950 p-6 hover:bg-[#e6b800]">
          <div className="font-mono-tech text-[10px] uppercase opacity-70 mb-2">// REVIEW</div>
          <div className="font-display text-2xl tracking-tighter">{stats.new_enquiries} NEW ENQUIRIES →</div>
        </Link>
      </div>
    </div>
  );
}

function PropertiesList() {
  const [props, setProps] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [missingOnly, setMissingOnly] = React.useState(false);

  const fetchAll = React.useCallback(() => {
    api.get("/properties?limit=200").then((r) => setProps(r.data));
  }, []);

  React.useEffect(fetchAll, [fetchAll]);

  const del = async (id) => {
    if (!confirm("Delete this property?")) return;
    await api.delete(`/properties/${id}`);
    toast.success("Deleted");
    fetchAll();
  };

  const isMissing = (p) => !p.images || p.images.length === 0;
  const missingCount = props.filter(isMissing).length;

  const filtered = props.filter((p) => {
    const matchesQ = !q || p.title.toLowerCase().includes(q.toLowerCase()) || p.reference.toLowerCase().includes(q.toLowerCase());
    const matchesMissing = !missingOnly || isMissing(p);
    return matchesQ && matchesMissing;
  });

  return (
    <div data-testid="admin-properties">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-1">// INVENTORY</div>
          <h1 className="font-display text-4xl tracking-tighter">PROPERTIES ({props.length})</h1>
        </div>
        <Link to="/admin/properties/new" data-testid="admin-new-property-btn" className="bg-[#031833] text-white font-mono-tech text-xs uppercase px-4 py-3 hover:bg-[#0A2455]">+ NEW PROPERTY</Link>
      </div>

      {missingCount > 0 && (
        <div
          data-testid="missing-photos-banner"
          className="mb-4 flex items-center justify-between gap-3 bg-amber-50 border-l-4 border-amber-500 px-4 py-3"
        >
          <div className="font-mono-tech text-xs text-amber-900">
            <strong>{missingCount}</strong> {missingCount === 1 ? "property is" : "properties are"} missing photos.
          </div>
          <button
            data-testid="toggle-missing-only"
            onClick={() => setMissingOnly((v) => !v)}
            className={`font-mono-tech text-[10px] uppercase px-3 py-1.5 border transition-colors ${
              missingOnly ? "bg-amber-900 text-white border-amber-900" : "border-amber-700 text-amber-900 hover:bg-amber-100"
            }`}
          >
            {missingOnly ? "Show all" : "Show only missing"}
          </button>
        </div>
      )}

      <input
        data-testid="admin-properties-search"
        placeholder="Search by title / reference..."
        value={q}
        onChange={(e) => setQ(e.target.value)}
        className="w-full md:max-w-md border border-zinc-300 px-3 py-2 font-mono-tech text-sm outline-none focus:border-[#031833] mb-4"
      />
      <div className="bg-white border border-zinc-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50">
            <tr className="font-mono-tech text-[10px] uppercase text-zinc-500">
              <th className="text-left p-3">Ref</th>
              <th className="text-left p-3">Title</th>
              <th className="text-left p-3">Zone</th>
              <th className="text-left p-3">Type</th>
              <th className="text-right p-3">Size</th>
              <th className="text-right p-3">Rent</th>
              <th className="text-center p-3">Status</th>
              <th className="text-right p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => {
              const missing = isMissing(p);
              return (
                <tr key={p.id} className={`border-t border-zinc-100 hover:bg-zinc-50 ${missing ? "bg-amber-50/40" : ""}`}>
                  <td className="p-3 font-mono-tech text-xs">{p.reference}</td>
                  <td className="p-3">
                    {p.title}
                    {p.featured && <span className="ml-2 bg-[#FFCC00] text-zinc-950 font-mono-tech text-[9px] px-1.5 py-0.5">★</span>}
                    {missing && (
                      <span
                        data-testid={`no-photo-badge-${p.reference}`}
                        title="This property has no photos — buyers will see a fallback image"
                        className="ml-2 inline-flex items-center gap-1 bg-red-600 text-white font-mono-tech text-[9px] uppercase tracking-wider px-1.5 py-0.5"
                      >
                        ⚠ NO PHOTOS
                      </span>
                    )}
                  </td>
                  <td className="p-3">{p.zone}</td>
                  <td className="p-3 capitalize">{p.property_type.replace("_", " ")}</td>
                  <td className="p-3 text-right font-mono-tech text-xs">{formatAED(p.size_sqft)} sqft</td>
                  <td className="p-3 text-right font-mono-tech text-xs">AED {formatAED(p.price_aed)}</td>
                  <td className="p-3 text-center"><span className={`font-mono-tech text-[10px] uppercase px-2 py-0.5 ${p.status === "available" ? "bg-green-100 text-green-800" : "bg-zinc-100"}`}>{p.status}</span></td>
                  <td className="p-3 text-right space-x-2">
                    <Link to={`/admin/properties/${p.id}/edit`} data-testid={`edit-${p.reference}`} className="font-mono-tech text-xs text-[#031833] hover:underline">Edit</Link>
                    <button onClick={() => del(p.id)} data-testid={`delete-${p.reference}`} className="font-mono-tech text-xs text-red-600 hover:underline">Delete</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="p-8 text-center font-mono-tech text-xs text-zinc-500">No properties found</div>}
      </div>
    </div>
  );
}

function PropertyForm({ editingId = null }) {
  const navigate = useNavigate();
  const [form, setForm] = React.useState({
    title: "", reference: "", property_type: "warehouse", zone: "Other",
    emirate: "Dubai", rent_sale: "rent", address: "",
    size_sqft: "", price_aed: "", description: "", features: "",
    parking: 5, height_meters: "", loading_bays: "", power_kw: "",
    has_chiller: false, has_office: true, featured: false, status: "available",
  });
  const [imagesList, setImagesList] = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (editingId) {
      api.get(`/properties/${editingId}`).then((r) => {
        const d = r.data;
        setForm({
          ...d,
          features: (d.features || []).join(", "),
        });
        setImagesList(d.images || []);
      });
    }
  }, [editingId]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const payload = {
      ...form,
      size_sqft: parseInt(form.size_sqft) || 0,
      price_aed: parseInt(form.price_aed) || 0,
      parking: parseInt(form.parking) || 0,
      height_meters: form.height_meters ? parseFloat(form.height_meters) : null,
      loading_bays: form.loading_bays ? parseInt(form.loading_bays) : null,
      power_kw: form.power_kw ? parseInt(form.power_kw) : null,
      features: form.features.split(",").map((s) => s.trim()).filter(Boolean),
      images: imagesList,
    };
    try {
      if (editingId) {
        await api.put(`/properties/${editingId}`, payload);
        toast.success("Property updated");
      } else {
        await api.post("/properties", payload);
        toast.success("Property created");
      }
      navigate("/admin/properties");
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const setField = (name, val) => setForm((prev) => ({ ...prev, [name]: val }));

  return (
    <div data-testid="admin-property-form">
      <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-1">// {editingId ? "EDIT" : "NEW"} PROPERTY</div>
      <h1 className="font-display text-4xl tracking-tighter mb-8">{editingId ? "EDIT" : "ADD"} PROPERTY</h1>
      <form onSubmit={submit} className="bg-white border border-zinc-200 p-6 grid md:grid-cols-2 gap-4 max-w-5xl">
        <AdminField label="Title *" value={form.title} onChange={(v) => setField("title", v)} required />
        <AdminField label="Reference *" value={form.reference} onChange={(v) => setField("reference", v)} required />
        <div>
          <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1">Type</label>
          <select value={form.property_type} onChange={(e) => setForm({ ...form, property_type: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm">
            {PROPERTY_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1">Zone</label>
          <select value={form.zone} onChange={(e) => setForm({ ...form, zone: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm">
            {ZONES.map((z) => <option key={z} value={z}>{z}</option>)}
          </select>
        </div>
        <div>
          <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1">Emirate</label>
          <select data-testid="admin-emirate-select" value={form.emirate || "Dubai"} onChange={(e) => setForm({ ...form, emirate: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm">
            {UAE_EMIRATES.map((em) => <option key={em.code} value={em.name}>{em.name}</option>)}
          </select>
        </div>
        <div>
          <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1">Listing</label>
          <select data-testid="admin-rent-sale-select" value={form.rent_sale || "rent"} onChange={(e) => setForm({ ...form, rent_sale: e.target.value })} className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-sm">
            <option value="rent">For Rent</option>
            <option value="sale">For Sale</option>
          </select>
        </div>
        <div className="md:col-span-2"><AdminField label="Address" value={form.address} onChange={(v) => setField("address", v)} /></div>
        <AdminField label="Size (sqft) *" value={form.size_sqft} onChange={(v) => setField("size_sqft", v)} type="number" required />
        <AdminField label={form.rent_sale === "sale" ? "Sale Price AED *" : "Annual Rent AED *"} value={form.price_aed} onChange={(v) => setField("price_aed", v)} type="number" required />
        <AdminField label="Clear Height (m)" value={form.height_meters} onChange={(v) => setField("height_meters", v)} type="number" step="0.1" />
        <AdminField label="Loading Bays" value={form.loading_bays} onChange={(v) => setField("loading_bays", v)} type="number" />
        <AdminField label="Power kW" value={form.power_kw} onChange={(v) => setField("power_kw", v)} type="number" />
        <AdminField label="Parking" value={form.parking} onChange={(v) => setField("parking", v)} type="number" />
        <div className="md:col-span-2"><AdminField label="Description" value={form.description} onChange={(v) => setField("description", v)} textarea /></div>
        <div className="md:col-span-2"><AdminField label="Features (comma-separated)" value={form.features} onChange={(v) => setField("features", v)} /></div>
        <div className="md:col-span-2">
          <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-2">Images</label>
          <AdminImageGallery images={imagesList} onChange={setImagesList} />
        </div>
        <div className="md:col-span-2 flex flex-wrap gap-6 items-center">
          <label className="flex items-center gap-2 font-mono-tech text-xs"><input type="checkbox" checked={form.has_chiller} onChange={(e) => setForm({ ...form, has_chiller: e.target.checked })} /> Has Chiller</label>
          <label className="flex items-center gap-2 font-mono-tech text-xs"><input type="checkbox" checked={form.has_office} onChange={(e) => setForm({ ...form, has_office: e.target.checked })} /> Has Office</label>
          <label className="flex items-center gap-2 font-mono-tech text-xs"><input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} /> Featured</label>
          <div>
            <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1">Status</label>
            <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="border border-zinc-300 px-3 py-1 font-mono-tech text-xs">
              <option value="available">available</option>
              <option value="reserved">reserved</option>
              <option value="rented">rented</option>
            </select>
          </div>
        </div>
        <div className="md:col-span-2 flex gap-3 mt-4">
          <button data-testid="admin-save-property" disabled={loading} className="bg-[#031833] text-white font-mono-tech text-sm uppercase px-6 py-3 disabled:opacity-50 hover:bg-[#0A2455]">{loading ? "SAVING..." : "SAVE PROPERTY →"}</button>
          <Link to="/admin/properties" className="font-mono-tech text-sm uppercase border border-zinc-300 px-6 py-3 hover:bg-zinc-100">CANCEL</Link>
        </div>
      </form>
    </div>
  );
}

function PropertyNew() { return <PropertyForm />; }
function PropertyEdit() {
  const id = window.location.pathname.split("/").slice(-2)[0];
  return <PropertyForm editingId={id} />;
}

function Enquiries() {
  const [items, setItems] = React.useState([]);
  const load = React.useCallback(() => {
    api.get("/admin/enquiries").then((r) => setItems(r.data));
  }, []);
  React.useEffect(load, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/enquiries/${id}?status=${status}`);
    toast.success("Status updated");
    load();
  };

  return (
    <div data-testid="admin-enquiries">
      <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-1">// INBOX</div>
      <h1 className="font-display text-4xl tracking-tighter mb-8">ENQUIRIES ({items.length})</h1>
      <div className="space-y-3">
        {items.map((e) => (
          <div key={e.id} className="bg-white border border-zinc-200 p-5">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-display text-xl tracking-tighter">{e.name}</div>
                <div className="font-mono-tech text-[10px] uppercase text-zinc-500">{new Date(e.created_at).toLocaleString()}</div>
              </div>
              <span className={`font-mono-tech text-[10px] uppercase px-2 py-1 ${e.status === "new" ? "bg-[#FFCC00]" : e.status === "contacted" ? "bg-blue-100 text-blue-800" : "bg-zinc-100"}`}>{e.status}</span>
            </div>
            <div className="grid md:grid-cols-3 gap-2 mb-3 text-sm">
              <div className="font-mono-tech text-xs"><span className="text-zinc-500">EMAIL:</span> {e.email}</div>
              <div className="font-mono-tech text-xs"><span className="text-zinc-500">PHONE:</span> {e.phone}</div>
              <div className="font-mono-tech text-xs"><span className="text-zinc-500">COMPANY:</span> {e.company || "—"}</div>
            </div>
            {e.property_ref && <div className="font-mono-tech text-xs text-[#031833] mb-2">PROPERTY: {e.property_ref}</div>}
            <p className="text-zinc-700 text-sm mb-3">{e.message}</p>
            <div className="flex gap-2 flex-wrap">
              {["new", "contacted", "qualified", "closed"].map((s) => (
                <button key={s} onClick={() => setStatus(e.id, s)} className={`font-mono-tech text-[10px] uppercase px-3 py-1 border ${e.status === s ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:bg-zinc-100"}`}>{s}</button>
              ))}
              <a href={`https://wa.me/${e.phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="font-mono-tech text-[10px] uppercase px-3 py-1 bg-[#25D366] text-white">WhatsApp</a>
              <a href={`tel:${e.phone}`} className="font-mono-tech text-[10px] uppercase px-3 py-1 bg-zinc-900 text-white">Call</a>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="border border-dashed border-zinc-300 p-10 text-center font-mono-tech text-xs">No enquiries yet</div>}
      </div>
    </div>
  );
}

function Submissions() {
  const [items, setItems] = React.useState([]);
  const load = React.useCallback(() => {
    api.get("/admin/asset-listings").then((r) => setItems(r.data));
  }, []);
  React.useEffect(load, [load]);

  const setStatus = async (id, status) => {
    await api.patch(`/admin/asset-listings/${id}?status=${status}`);
    toast.success("Status updated");
    load();
  };

  return (
    <div data-testid="admin-submissions">
      <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-1">// OWNER SUBMISSIONS</div>
      <h1 className="font-display text-4xl tracking-tighter mb-8">SUBMISSIONS ({items.length})</h1>
      <div className="space-y-3">
        {items.map((s) => (
          <div key={s.id} className="bg-white border border-zinc-200 p-5">
            <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="font-display text-xl tracking-tighter">{s.owner_name} {s.company && <span className="text-zinc-500 text-sm">· {s.company}</span>}</div>
                <div className="font-mono-tech text-[10px] uppercase text-zinc-500">{new Date(s.created_at).toLocaleString()}</div>
              </div>
              <span className={`font-mono-tech text-[10px] uppercase px-2 py-1 ${s.status === "new" ? "bg-[#FFCC00]" : s.status === "listed" ? "bg-green-100 text-green-800" : "bg-zinc-100"}`}>{s.status}</span>
            </div>
            <div className="grid md:grid-cols-3 gap-2 mb-3">
              <div className="font-mono-tech text-xs"><span className="text-zinc-500">EMAIL:</span> {s.owner_email}</div>
              <div className="font-mono-tech text-xs"><span className="text-zinc-500">PHONE:</span> {s.owner_phone}</div>
              <div className="font-mono-tech text-xs"><span className="text-zinc-500">ZONE:</span> {s.zone}</div>
              <div className="font-mono-tech text-xs"><span className="text-zinc-500">TYPE:</span> {s.property_type}</div>
              <div className="font-mono-tech text-xs"><span className="text-zinc-500">SIZE:</span> {formatAED(s.size_sqft)} sqft</div>
              <div className="font-mono-tech text-xs"><span className="text-zinc-500">EXPECTED:</span> {s.expected_rent_aed ? `AED ${formatAED(s.expected_rent_aed)}` : "flexible"}</div>
            </div>
            <div className="font-mono-tech text-xs text-zinc-700 mb-2"><span className="text-zinc-500">ADDRESS:</span> {s.address}</div>
            {s.features?.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {s.features.map((f, i) => <span key={i} className="border border-zinc-300 px-2 py-0.5 font-mono-tech text-[10px]">{f}</span>)}
              </div>
            )}
            {s.notes && <p className="text-zinc-700 text-sm mb-3">{s.notes}</p>}
            {s.images?.length > 0 && (
              <div className="flex gap-2 mb-3 flex-wrap">
                {s.images.map((img, i) => (
                  <img key={i} src={resolveImg(img)} alt="" className="w-20 h-20 object-cover border border-zinc-200" />
                ))}
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
              {["new", "reviewing", "listed", "rejected"].map((st) => (
                <button key={st} onClick={() => setStatus(s.id, st)} data-testid={`submission-status-${st}-${s.id}`} className={`font-mono-tech text-[10px] uppercase px-3 py-1 border ${s.status === st ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-300 hover:bg-zinc-100"}`}>{st}</button>
              ))}
              <a href={`https://wa.me/${s.owner_phone.replace(/\D/g, "")}`} target="_blank" rel="noopener noreferrer" className="font-mono-tech text-[10px] uppercase px-3 py-1 bg-[#25D366] text-white">WhatsApp Owner</a>
              <a href={`mailto:${s.owner_email}`} className="font-mono-tech text-[10px] uppercase px-3 py-1 bg-zinc-900 text-white">Email</a>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="border border-dashed border-zinc-300 p-10 text-center font-mono-tech text-xs">No owner submissions yet</div>}
      </div>
    </div>
  );
}

function ImportSheet() {
  const [url, setUrl] = React.useState("https://docs.google.com/spreadsheets/d/1_vvoP2NXqRD6OrDLccg4jY_LCJrL1I9bKQ1__fXD-Z8/edit?usp=sharing");
  const [replace, setReplace] = React.useState(true);
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState(null);

  const run = async () => {
    if (replace) {
      const ok = window.confirm(
        "Replace mode will DELETE all existing properties and re-create them from the sheet.\n\n" +
        "Properties without a manually-uploaded photo will be reset to default stock images.\n\n" +
        "✅ Photos you uploaded via admin will be PRESERVED automatically (auto-relinked by reference).\n\n" +
        "Continue?"
      );
      if (!ok) return;
    }
    setBusy(true); setError(null); setResult(null);
    try {
      const res = await api.post("/admin/import-sheet", { sheet_url: url, replace });
      setResult(res.data);
      toast.success(`Imported ${res.data.inserted} properties`);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Import failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-import-sheet">
      <div className="font-mono-tech text-[10px] uppercase text-[#031833] mb-1">// GOOGLE SHEET SYNC</div>
      <h1 className="font-display text-4xl tracking-tighter mb-6">IMPORT INVENTORY</h1>
      <div className="bg-white border border-zinc-200 p-6 max-w-3xl space-y-4">
        <div>
          <label className="font-mono-tech text-[10px] uppercase text-zinc-500 block mb-1">Google Sheet URL</label>
          <input
            data-testid="import-sheet-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://docs.google.com/spreadsheets/d/..."
            className="w-full border border-zinc-300 px-3 py-2 font-mono-tech text-xs outline-none focus:border-[#031833]"
          />
          <p className="font-mono-tech text-[10px] text-zinc-500 mt-1">
            Sheet must be shared &quot;Anyone with link → Viewer&quot;. Expected columns: LOCATION, SIZE, PRICE, ELECTRIC LOAD, CEILING HEIGHT, TYPE, etc.
          </p>
        </div>
        <label className="flex items-center gap-2 font-mono-tech text-xs">
          <input data-testid="import-sheet-replace" type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} />
          Replace all existing properties (uncheck to upsert by reference)
        </label>
        <div className="bg-amber-50 border-l-4 border-amber-500 px-3 py-2 font-mono-tech text-[11px] text-amber-900 leading-relaxed">
          ℹ️ Manually-uploaded property photos are <strong>automatically preserved</strong> across imports — the system re-links them by property reference (e.g. <code>BW-DIP-001</code>) after the sheet load.
        </div>
        <button
          data-testid="import-sheet-run"
          onClick={run}
          disabled={busy || !url.trim()}
          className="bg-[#031833] text-white font-mono-tech text-sm uppercase px-6 py-3 disabled:opacity-50 hover:bg-[#0A2455]"
        >
          {busy ? "IMPORTING..." : "RUN IMPORT →"}
        </button>

        {result && (
          <div className="bg-green-50 border border-green-200 p-4 font-mono-tech text-xs">
            <div className="text-green-800 font-semibold mb-1">✓ Import complete</div>
            <div>Inserted: <b>{result.inserted}</b></div>
            <div>Skipped (missing size/price): {result.skipped}</div>
            <div>Total rows in sheet: {result.total_rows}</div>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 p-4 font-mono-tech text-xs text-red-800">
            ✗ {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  return (
    <AdminShell>
      <Routes>
        <Route index element={<Dashboard />} />
        <Route path="properties" element={<PropertiesList />} />
        <Route path="properties/new" element={<PropertyNew />} />
        <Route path="properties/:id/edit" element={<PropertyEdit />} />
        <Route path="import" element={<ImportSheet />} />
        <Route path="enquiries" element={<Enquiries />} />
        <Route path="submissions" element={<Submissions />} />
        <Route path="market/*" element={<AdminMarket />} />
        <Route path="insights" element={<AdminInsights />} />
        <Route path="change-password" element={<AdminChangePassword />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminShell>
  );
}
